import { func, number, oneOfType, shape, string, bool } from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import _get from 'lodash/get';

import Row from 'ravenjs/lib/Row';
import Col from 'ravenjs/lib/Col';
import Button from 'ravenjs/lib/Button';
import Typography from 'ravenjs/lib/Typography';
import { isEmpty } from 'ravenjs/utils/lodash';
import { sanitizeMarkup } from 'ravenjs/utils/sanitize';
import { extractErrorMessage } from 'ravenjs/utils/error';
import { toast } from 'ravenjs/lib/Toaster';
import { COLORS } from 'ravenjs/constants/colors';

import { DEFAULT_COLORS } from 'constants/colors';
import MESSAGES from 'constants/messages';
import { actions as adminActions } from 'modules/admin';
import { actions as uiActions } from 'modules/ui';
import { isOasisCompany } from 'utils/company';

const LoginInstructionStyle = styled(Row)`
    border-left: 4px solid ${DEFAULT_COLORS.BLUE};
    padding-left: 1.25rem;
    margin: 1.2rem 7.75rem;
    overflow-wrap: anywhere;
    blockquote {
        border: 0.001rem solid ${COLORS.GREY_3};
        border-left: 0.3rem solid ${COLORS.GREY_3};
        padding: 1rem;
        border-radius: 0.3rem;
        width: fit-content;
    }
    a {
        color: ${DEFAULT_COLORS.LINK};
    }
    ol,
    ul {
        text-align: left;
    }
`;

const LoginButtonStyled = styled(Button)`
    line-height: 1.67;
    letter-spacing: normal;
    height: 36px;
    width: 92px;
    font-weight: 500;
    font-size: 0.75rem;
    border: solid 1px ${DEFAULT_COLORS.ORANGE};
    border-radius: 0;
`;

const WelcomeIntroStyled = styled(Typography)`
    p,
    div {
        font-size: 1.125rem;
        font-weight: 300;
    }
    ul {
        font-weight: 300;
        font-size: 1.125rem;
    }
    blockquote {
        border: 0.001rem solid ${COLORS.GREY_3};
        border-left: 0.3rem solid ${COLORS.GREY_3};
        padding: 1rem;
        border-radius: 0.3rem;
        width: fit-content;
    }
    pre {
        width: fit-content;
    }
    // Using default styles for 'p' tag. This ensures consistency with email templates on different client apps.
    p {
        display: block;
        margin: 1em 0;
    }
`;

const CreatePasswordButtonStyled = styled(Button)`
    border-radius: 0;
    line-height: 1.67;
    letter-spacing: normal;
    height: 36px;
    width: 187px;
    font-weight: 500;
    font-size: 0.75rem;
`;

function WelcomeEmailPreview(props) {
    const {
        emailTemplate,
        partnerId,
        fetchPartnerSettings,
        isLoading,
        disableCreatePasswordLink,
    } = props;
    const {
        body,
        headerLogo,
        defaultLogo,
        customLogo,
        loginInstruction,
        loginLink,
        createPasswordLink,
        footer,
    } = emailTemplate;

    const [displayPartnerOnlyLogo, setPartnerOnlyLogo] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    const hasCustomCompanyChecks = () => {
        return displayPartnerOnlyLogo || isOasisCompany(Number(partnerId));
    };

    const getPartnerSettings = useCallback(async () => {
        try {
            isLoading(true);
            const partnerSettings = await fetchPartnerSettings(partnerId);
            setPartnerOnlyLogo(_get(partnerSettings, 'displayPartnerOnlyLogo', false));
        } catch (e) {
            toast.error(extractErrorMessage(e));
        } finally {
            isLoading(false);
            setShowPreview(true);
        }
    }, [fetchPartnerSettings, isLoading, partnerId]);

    useEffect(() => {
        getPartnerSettings();
    }, [getPartnerSettings]);

    return (
        showPreview && (
            <Row
                margin="0"
                direction="column"
                alignItems="center"
                style={{
                    backgroundColor: hasCustomCompanyChecks()
                        ? DEFAULT_COLORS.GREY_6
                        : DEFAULT_COLORS.LIGHT_BANNER,
                }}
            >
                <Col
                    margin="4.5rem 2.5rem 0 2.5rem"
                    alignSelf="center"
                    style={{ backgroundColor: DEFAULT_COLORS.WHITE }}
                >
                    <Row justify="center">
                        <Col padding="0" style={{ textAlign: 'center' }}>
                            <img
                                src={headerLogo}
                                alt="logo"
                                height={displayPartnerOnlyLogo ? '39px' : 'auto'}
                                width={hasCustomCompanyChecks() ? '191px' : '100%'}
                                style={{ marginTop: hasCustomCompanyChecks() ? '1rem' : 0 }}
                                loading="lazy"
                            />
                        </Col>
                    </Row>
                    {!isEmpty(defaultLogo) && !isEmpty(customLogo) && (
                        <Row alignItems="center" margin="2rem 0 0 0">
                            <Col alignSelf="center" padding="1rem 1.5rem 0 10rem">
                                <img
                                    src={defaultLogo}
                                    alt="logo"
                                    height="auto"
                                    width={hasCustomCompanyChecks() ? '50%' : '100%'}
                                />
                            </Col>
                            <Col alignSelf="center" padding="1rem 10rem 0 1.5rem">
                                <img src={customLogo} alt="logo" height="auto" width="100%" />
                            </Col>
                        </Row>
                    )}
                    <Row margin="0" direction="column">
                        <WelcomeIntroStyled
                            align="center"
                            gutterTop="1.8rem"
                            lineHeight="1.563"
                            padding="0 3.75rem 0 3.75rem"
                            fontFamily="Roboto"
                            dangerouslySetInnerHTML={sanitizeMarkup(body)}
                        />
                    </Row>
                    <LoginInstructionStyle>
                        <Typography
                            align="left"
                            gutterTop="8px"
                            lineHeight="1.563"
                            fontFamily="Roboto"
                            fontSize="1.125rem"
                            dangerouslySetInnerHTML={sanitizeMarkup(loginInstruction)}
                        />
                    </LoginInstructionStyle>
                    <Row direction="column" alignItems="center" margin="0">
                        {createPasswordLink && (
                            <CreatePasswordButtonStyled
                                margin="1.3rem 0 2rem 0"
                                color="emailPrimaryButton"
                                onClick={() => window.open(createPasswordLink)}
                                disabled={disableCreatePasswordLink}
                            >
                                {MESSAGES.EMAIL_PREVIEW.BUTTONS.CREATE_PASSWORD}
                            </CreatePasswordButtonStyled>
                        )}
                        {loginLink && (
                            <>
                                <Typography
                                    gutterTop="0.5rem"
                                    fontFamily="Roboto"
                                    fontSize="1.125rem"
                                >
                                    {MESSAGES.EMAIL_PREVIEW.BODY.ALREADY_HAVE_PASSWORD}
                                </Typography>
                                <LoginButtonStyled
                                    margin="2rem 0 4.5rem 0"
                                    color="emailSecondaryButton"
                                    onClick={() => window.open(loginLink)}
                                    noMinWidth
                                >
                                    {MESSAGES.EMAIL_PREVIEW.BUTTONS.LOGIN}
                                </LoginButtonStyled>
                            </>
                        )}
                    </Row>
                </Col>
                <Col margin="2rem 0 2rem 0" alignSelf="center">
                    <Typography
                        align="center"
                        gutterTop="0"
                        gutterBottom="0"
                        fontFamily="Roboto"
                        fontSize="0.75rem"
                        dangerouslySetInnerHTML={sanitizeMarkup(footer)}
                    />
                </Col>
            </Row>
        )
    );
}

WelcomeEmailPreview.propTypes = {
    emailTemplate: shape({
        body: string,
        headerLogo: string,
        defaultLogo: string,
        customLogo: string,
        loginInstruction: string,
        loginLink: string,
        createPasswordLink: string,
        footer: string,
    }),
    fetchPartnerSettings: func.isRequired,
    isLoading: func.isRequired,
    disableCreatePasswordLink: bool,
    partnerId: oneOfType([number, string]).isRequired,
};

WelcomeEmailPreview.defaultProps = {
    emailTemplate: null,
    disableCreatePasswordLink: false,
};

const mapDispatchToProps = {
    fetchPartnerSettings: adminActions.fetchPartnerSettings,
    isLoading: uiActions.isLoading,
};

export default connect(null, mapDispatchToProps)(WelcomeEmailPreview);
